import React, { useRef, useEffect } from 'react';

/**
 * PinInput - 6-digit PIN input with individual boxes
 * Auto-advance on digit entry, backspace goes back, masked with dots
 */
export default function PinInput({ value, onChange, disabled, autoFocus }) {
  const inputRefs = useRef([]);
  const digits = (value || '').split('').concat(Array(6).fill('')).slice(0, 6);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index, e) => {
    const val = e.target.value.replace(/\D/g, '');
    if (!val) return;
    const char = val.slice(-1);
    const newDigits = [...digits];
    newDigits[index] = char;
    const newValue = newDigits.join('').replace(/\s/g, '');
    onChange(newValue);
    if (index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newDigits = [...digits];
      if (newDigits[index]) {
        newDigits[index] = '';
        onChange(newDigits.join('').replace(/\s/g, ''));
      } else if (index > 0) {
        newDigits[index - 1] = '';
        onChange(newDigits.join('').replace(/\s/g, ''));
        inputRefs.current[index - 1].focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) {
      onChange(pasted);
      const focusIdx = Math.min(pasted.length, 5);
      if (inputRefs.current[focusIdx]) inputRefs.current[focusIdx].focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center" data-testid="pin-input">
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={el => inputRefs.current[i] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit ? '\u25CF' : ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          className="w-11 h-12 text-center text-lg font-semibold rounded-xl border-2 outline-none transition-all"
          style={{
            borderColor: digit ? 'var(--ios-accent, #e03c3c)' : 'var(--ios-border-subtle, #e5e5e5)',
            background: 'var(--ios-bg-secondary, #fff)',
            color: 'var(--ios-text-primary, #1D1D1F)',
          }}
          data-testid={`pin-digit-${i}`}
        />
      ))}
    </div>
  );
}
